// OverlaySvg — per-side dots + skeleton over the video. Spec §6.3.3, §7.1, §7.3.
//
// Keypoint visibility is filtered per side:
//   front_left / front_right : all 10 keypoints
//   left                     : left_shoulder, left_elbow, left_wrist
//   right                    : right_shoulder, right_elbow, right_wrist
//
// Qualified dots are selectable by clicking but locked for drag by default.
// Press Space to unlock the selected qualified joint; press again to re-lock.
// Press L-Shift to toggle free-drag (bypasses epipolar constraint).
//
// Epipolar curve is always shown when available. Drag snaps to it unless
// freeDrag is active. Dot fill follows joint family; the outline follows
// pred.qualified so confidence stays visible even after manual edits.

import * as React from 'react';
import {
  COLORS,
  DOT_RADIUS_PX,
  KEYPOINT_COLORS,
  KEYPOINT_SHAPES,
  LOCKED_DOT_ALPHA,
  SELECTED_DOT_RADIUS_PX,
} from '../../../config/constants';
import { KEYPOINTS, SKELETON_EDGES, visibleKeypoints } from '../../../config/keypoints';
import { useEpipolarCurve } from '../../../hooks/useEpipolarCurve';
import { screenToImage } from '../../../hooks/useImageCoords';
import { snapToCurve, splitCurveOnGaps } from '../../../lib/epipolar';
import { edgeSide, starPoints } from '../../../lib/shapes';
import { useStore } from '../../../store';
import type { Side } from '../../../store/predictions';
import type { EditedKeypoint, EditMap, FrameMap, KeypointName } from '../../../types';

export interface OverlaySvgProps {
  side: Side;
  onDragStateChange?: (dragging: boolean) => void;
  readOnly?: boolean;
}

interface EffectiveDot {
  xy: [number, number];
  visibility: EditedKeypoint['visibility'];
  locked: boolean; // manually locked by the user
  qualified: boolean; // high-confidence prediction (determines outline)
  hasEdit: boolean; // has a manual edit (qualified keypoint has been unlocked)
}

function effectiveDot(
  preds: FrameMap | null,
  edits: EditMap,
  frame: number,
  name: KeypointName
): EffectiveDot | null {
  const pred = preds?.[frame]?.find((r) => r.name === name);
  if (!pred) return null;
  const edit = edits[frame]?.[name];
  if (edit) {
    return {
      xy: edit.xy,
      visibility: edit.visibility,
      locked: edit.locked,
      qualified: pred.qualified, // outline follows prediction, not edit presence
      hasEdit: true,
    };
  }
  return {
    xy: pred.xy,
    visibility: pred.defaultVisibility,
    locked: false,
    qualified: pred.qualified,
    hasEdit: false,
  };
}

export function OverlaySvg({
  side,
  onDragStateChange,
  readOnly = false,
}: OverlaySvgProps): React.ReactElement | null {
  // Read this side's native size (populated by VideoCanvas/ImageCanvas on
  // load) so the SVG viewBox and screenToImage math use the camera's own
  // pixel coordinate space — important when cameras differ in size.
  const imageSize = useStore((s) => s.imageSizes[side] ?? s.imageSize);
  const seekInFlight = useStore((s) => s.seekInFlight);
  const currentFrame = useStore((s) => s.currentFrame);
  const showSkeleton = useStore((s) => s.showSkeleton);
  const freeDrag = useStore((s) => s.freeDrag);
  const preds = useStore((s) => {
    if (side === 'front_left') return s.predFrontLeft;
    if (side === 'front_right') return s.predFrontRight;
    if (side === 'left') return s.predLeft;
    return s.predRight;
  });
  const edits = useStore((s) => {
    if (side === 'front_left') return s.editsFrontLeft;
    if (side === 'front_right') return s.editsFrontRight;
    if (side === 'left') return s.editsLeft;
    return s.editsRight;
  });
  const selected = useStore((s) => {
    if (side === 'front_left') return s.selectedFrontLeft;
    if (side === 'front_right') return s.selectedFrontRight;
    if (side === 'left') return s.selectedLeft;
    return s.selectedRight;
  });
  const selectKeypoint = useStore((s) => s.selectKeypoint);
  const setActiveView = useStore((s) => s.setActiveView);
  const setKeypoint = useStore((s) => s.setKeypoint);

  const epipolar = useEpipolarCurve(side);

  const svgRef = React.useRef<SVGSVGElement>(null);
  const dragKeypoint = React.useRef<KeypointName | null>(null);

  const [h, w] = imageSize;

  const visible = visibleKeypoints(side);

  // beginDrag: always select + activate, only start drag when not locked/qualified-default.
  const beginDrag = (
    e: React.PointerEvent<SVGCircleElement>,
    name: KeypointName,
    effectiveLocked: boolean
  ): void => {
    selectKeypoint(side, name);
    setActiveView(side);
    if (effectiveLocked) return;
    dragKeypoint.current = name;
    onDragStateChange?.(true);
    try {
      (e.currentTarget as Element).setPointerCapture(e.pointerId);
    } catch {
      // jsdom or browsers without capture support — selection still works.
    }
  };

  const moveDrag = (e: React.PointerEvent<SVGCircleElement>): void => {
    const name = dragKeypoint.current;
    if (name === null) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    let [u, v] = screenToImage(e.clientX, e.clientY, rect, [h, w]);

    // Constrain to epipolar curve unless freeDrag is active.
    if (!freeDrag && epipolar.curve && epipolar.anchorName === name) {
      const snapped = snapToCurve(u, v, epipolar.curve);
      if (snapped) [u, v] = snapped;
    }

    const existing = edits[currentFrame]?.[name];
    setKeypoint(side, currentFrame, name, {
      xy: [u, v],
      visibility: existing?.visibility ?? 'visible',
      locked: existing?.locked ?? false,
    });
  };

  const endDrag = (e: React.PointerEvent<SVGCircleElement>): void => {
    if (dragKeypoint.current === null) return;
    dragKeypoint.current = null;
    onDragStateChange?.(false);
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {
      // Capture may already be released (e.g. pointercancel); ignore.
    }
  };

  if (h <= 0 || w <= 0) return null;

  const scale = w / 600;
  const dotR = DOT_RADIUS_PX * scale;
  const selR = SELECTED_DOT_RADIUS_PX * scale;
  const edgeStroke = (DOT_RADIUS_PX * scale) / 2;
  const glowR = selR * 1.6;
  const hitR = Math.max(dotR * 2, selR);

  return (
    <svg
      ref={svgRef}
      data-testid={`overlay-${side}`}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="xMidYMid meet"
      className="pointer-events-none absolute inset-0 h-full w-full"
    >
      <g opacity={seekInFlight ? 0.4 : 1}>
        {!readOnly &&
          epipolar.curve &&
          splitCurveOnGaps(epipolar.curve).map((segment, i) => (
            <polyline
              key={`epiline-${i}`}
              points={segment.map(([x, y]) => `${x},${y}`).join(' ')}
              stroke={COLORS.epipolarCurve}
              strokeWidth={edgeStroke}
              fill="none"
              opacity={freeDrag ? 0.25 : 0.75}
              pointerEvents="none"
            />
          ))}
        {showSkeleton &&
          SKELETON_EDGES.map(([aName, bName]) => {
            if (!visible.has(aName) || !visible.has(bName)) return null;
            const a = effectiveDot(preds, edits, currentFrame, aName);
            const b = effectiveDot(preds, edits, currentFrame, bName);
            if (!a || !b) return null;
            if (a.visibility === 'out_of_frame' || b.visibility === 'out_of_frame') {
              return null;
            }
            const side = edgeSide(aName, bName);
            const stroke =
              side === 'left'
                ? COLORS.edgeLeft
                : side === 'right'
                  ? COLORS.edgeRight
                  : COLORS.edgeCross;
            return (
              <line
                key={`edge-${aName}-${bName}`}
                x1={a.xy[0]}
                y1={a.xy[1]}
                x2={b.xy[0]}
                y2={b.xy[1]}
                stroke={stroke}
                strokeWidth={edgeStroke}
              />
            );
          })}
        {KEYPOINTS.map(({ name }) => {
          if (!visible.has(name)) return null;
          const d = effectiveDot(preds, edits, currentFrame, name);
          if (!d || d.visibility === 'out_of_frame') return null;

          const isSelected = selected === name;
          const fill = KEYPOINT_COLORS[name];
          const shape = KEYPOINT_SHAPES[name];
          const opacity = d.locked ? LOCKED_DOT_ALPHA : 1;
          const [cx, cy] = d.xy;
          const r = isSelected ? selR : dotR;

          // Qualified keypoints with no edit are selectable but not draggable.
          const qualifiedDefault = d.qualified && !d.hasEdit;
          const effectiveLocked = d.locked || qualifiedDefault;
          const cursor = d.locked ? 'not-allowed' : qualifiedDefault ? 'pointer' : 'grab';

          // Pure-fill marker, shape per joint family (no confidence outline).
          const markerProps = {
            'data-testid': `dot-${side}-${name}`,
            fill,
            opacity,
            pointerEvents: 'none' as const,
          };

          return (
            <React.Fragment key={`dot-${name}`}>
              {isSelected && (
                <circle
                  data-testid={`glow-${side}-${name}`}
                  cx={cx}
                  cy={cy}
                  r={glowR}
                  fill={COLORS.selected}
                  opacity={0.35}
                  pointerEvents="none"
                />
              )}
              {shape === 'circle' && <circle cx={cx} cy={cy} r={r} {...markerProps} />}
              {shape === 'square' && (
                <rect x={cx - r} y={cy - r} width={r * 2} height={r * 2} {...markerProps} />
              )}
              {shape === 'star' && <polygon points={starPoints(cx, cy, r)} {...markerProps} />}
              {/* Hit target — absent in readOnly views */}
              {!readOnly && (
                <circle
                  data-testid={`hit-${side}-${name}`}
                  cx={d.xy[0]}
                  cy={d.xy[1]}
                  r={hitR}
                  fill="transparent"
                  style={{ pointerEvents: 'auto', cursor }}
                  onPointerDown={(e) => beginDrag(e, name, effectiveLocked)}
                  onPointerMove={moveDrag}
                  onPointerUp={endDrag}
                  onPointerCancel={endDrag}
                />
              )}
            </React.Fragment>
          );
        })}
      </g>
    </svg>
  );
}
